/**
 * URL builder functions organized by domain.
 * Each function returns a relative endpoint path.
 * No string concatenation at call sites -- always use these functions.
 *
 * Pattern: endpoints.{domain}.{action}(params)
 */

export const endpoints = {
  /** Auth endpoints */
  auth: {
    session: () => '/auth/session',
    login: () => '/auth/session/login',
    selectOrganization: () => '/auth/session/select-organization',
    createOrganization: () => '/auth/organizations',
    logout: () => '/auth/logout',
    ssoEventsConnect: () => '/auth/sso/events/connect',
    orgSyncStatus: () => '/auth/session/org-sync-status',
  },

  /** Ad account management */
  accounts: {
    list: (organizationId: string) => `/auth/accounts/${organizationId}`,
    detail: (organizationId: string) => `/auth/accounts/${organizationId}`,
    connect: (platform: string) => `/auth/${platform}/connect`,
    disconnect: (platform: string, adAccountId: string) =>
      `/auth/${platform}/disconnect/${adAccountId}`,
    sync: (id: string) => `/accounts/${id}/sync`,
    oauthCallback: (platform: string) => `/auth/${platform}/callback`,
    byPlatform: (organizationId: string) => `/auth/accounts/${organizationId}`,
    metaPixels: (id: string) => `/ad-accounts/${id}/meta-pixels`,
    defaultPixel: (id: string) => `/ad-accounts/${id}/default-pixel`,
    metaPages: (id: string) => `/ad-accounts/${id}/meta-pages`,
    metaPagePosts: (id: string, pageId: string) => `/ad-accounts/${id}/meta-pages/${pageId}/posts`,
    defaultPage: (id: string) => `/ad-accounts/${id}/default-page`,
  },

  /** Event data (from events.com platform) */
  events: {
    list: () => '/events',
    detail: (id: string) => `/events/${id}`,
  },

  /** Campaign management */
  campaigns: {
    list: () => '/campaigns',
    create: () => '/campaigns',
    detail: (id: string) => `/campaigns/${id}`,
    update: (id: string) => `/campaigns/${id}`,
    delete: (id: string) => `/campaigns/${id}`,
    submit: (id: string) => `/campaigns/${id}/launch`,
    submitForReview: (id: string) => `/campaigns/${id}/submit-for-review`,
    pause: (id: string) => `/campaigns/${id}/pause`,
    resume: (id: string) => `/campaigns/${id}/resume`,
    syncStatus: (id: string) => `/campaigns/${id}/sync-status`,
    validateLanding: (id: string) => `/campaigns/${id}/validate-landing`,
    aiSuggestions: (id: string) => `/campaigns/${id}/ai-suggestions`,
    applySuggestion: (id: string, suggestionId: string) =>
      `/campaigns/${id}/ai-suggestions/${suggestionId}/apply`,
    duplicate: (id: string) => `/campaigns/${id}/duplicate`,
    paymentIntent: (id: string) => `/campaigns/${id}/payment-intent`,
    payment: (id: string) => `/campaigns/${id}/payment`,
    paymentCapture: (id: string) => `/campaigns/${id}/payment/capture`,
    paymentCancel: (id: string) => `/campaigns/${id}/payment/cancel`,
  },

  /** Targeting data for campaign wizard */
  targeting: {
    interests: (query: string) => `/targeting/interests?q=${encodeURIComponent(query)}`,
    behaviors: (query: string) => `/targeting/behaviors?q=${encodeURIComponent(query)}`,
    locations: (query: string) => `/targeting/locations?q=${encodeURIComponent(query)}`,
    audienceEstimate: () => '/targeting/audience-estimate',
  },

  /** Google Ads platform-specific search */
  adsGoogle: {
    // adAccountId omitted in the managed workflow — backend falls back to a
    // managed Google account for suggestions.
    keywords: (adAccountId: string | undefined, query: string) => {
      const account = adAccountId ? `adAccountId=${encodeURIComponent(adAccountId)}&` : '';
      return `/ads/google/keywords?${account}q=${encodeURIComponent(query)}`;
    },
    audiences: (
      adAccountId: string | undefined,
      query: string,
      type?: 'USER_INTEREST' | 'AUDIENCE' | 'ALL',
    ) => {
      const account = adAccountId ? `adAccountId=${encodeURIComponent(adAccountId)}&` : '';
      const base = `/ads/google/audiences?${account}q=${encodeURIComponent(query)}`;
      return type ? `${base}&type=${type}` : base;
    },
    geoTargets: (adAccountId: string | undefined, query: string) => {
      const account = adAccountId ? `adAccountId=${encodeURIComponent(adAccountId)}&` : '';
      return `/ads/google/geo-targets?${account}q=${encodeURIComponent(query)}`;
    },
  },

  /** AI suggestion endpoints */
  ai: {
    budgetSuggestion: () => '/ai/budget',
    audienceSuggestion: () => '/ai/audience',
    copySuggestion: () => '/ai/copy',
    imageSuggestion: () => '/ai/images',
    assetAccess: (key: string) => `/ai/assets/access?key=${encodeURIComponent(key)}`,
  },

  /** Creative assets */
  creatives: {
    byCampaign: (campaignId: string) => `/campaigns/${campaignId}/creatives`,
    create: (campaignId: string) => `/campaigns/${campaignId}/creatives`,
    detail: (campaignId: string, creativeId: string) =>
      `/campaigns/${campaignId}/creatives/${creativeId}`,
    update: (campaignId: string, creativeId: string) =>
      `/campaigns/${campaignId}/creatives/${creativeId}`,
    delete: (campaignId: string, creativeId: string) =>
      `/campaigns/${campaignId}/creatives/${creativeId}`,
    upload: (campaignId: string) => `/campaigns/${campaignId}/creatives/upload`,
    aiGenerate: (campaignId: string) => `/campaigns/${campaignId}/creatives/ai-generate`,
  },

  /** Creative library (standalone, org-level) */
  creativeLibrary: {
    list: (organizationId: string) =>
      `/creatives?organizationId=${encodeURIComponent(organizationId)}`,
    create: () => '/creatives',
    detail: (id: string) => `/creatives/${id}`,
    /**
     * Manual image upload for the campaign wizard. Mirrors the response
     * shape of AI-generated images so the wizard treats them uniformly.
     */
    uploadImage: () => '/creatives/upload',
    /** Mint a presigned S3 PUT URL for a direct video upload (browser→S3). */
    uploadUrl: () => '/creatives/upload-url',
    update: (id: string) => `/creatives/${id}`,
    delete: (id: string) => `/creatives/${id}`,
    campaigns: (id: string) => `/creatives/${id}/campaigns`,
  },

  /** Analytics & reporting */
  analytics: {
    dashboard: (organizationId: string) => `/analytics/organizations/${organizationId}`,
    campaign: (id: string) => `/analytics/campaigns/${id}`,
    export: () => '/analytics/export',
    platformBreakdown: () => '/analytics/platform-breakdown',
  },

  /** Notifications */
  notifications: {
    list: (organizationId: string) => `/notifications/${organizationId}`,
    markRead: (id: string) => `/notifications/${id}/read`,
    markAllRead: (organizationId: string) => `/notifications/${organizationId}/read-all`,
    unreadCount: (organizationId: string) => `/notifications/${organizationId}`,
    sseStream: (organizationId: string) => `/notifications/${organizationId}/stream`,
    preferences: (organizationId: string) => `/notifications/${organizationId}/preferences`,
  },

  /** Admin / internal */
  admin: {
    dashboard: () => '/admin/dashboard',
    campaigns: () => '/admin/campaigns',
    reviewCampaign: (id: string) => `/admin/campaigns/${id}/review`,
    managedAccounts: () => '/admin/managed-accounts',
    approveManagedAccount: (orgId: string) => `/admin/managed-accounts/${orgId}/approve`,
    rejectManagedAccount: (orgId: string) => `/admin/managed-accounts/${orgId}/reject`,
  },
} as const;
